import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import CoachingInfo from "@/components/coaching/CoachingInfo";
import CoachingPricing from "@/components/coaching/CoachingPricing";
import CoachingCalendar from "@/components/coaching/CoachingCalendar";
import CoachingNotifications from "@/components/coaching/CoachingNotifications";

const BookCoaching = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const calendlyRef = useRef<HTMLDivElement>(null);
  const [hasExistingSession, setHasExistingSession] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitiatingPayment, setIsInitiatingPayment] = useState(false);

  useEffect(() => {
    // Check if user has existing coaching sessions
    const checkExistingSessions = async () => {
      if (user) {
        try {
          setIsLoading(true);
          const { data, error } = await supabase
            .from('candidate_sessions')
            .select('*')
            .eq('user_id', user.id)
            .eq('session_type', 'coaching')
            .single();
          
          if (error && error.code !== 'PGRST116') {
            console.error('Error checking sessions:', error);
          }
          
          setHasExistingSession(!!data);
        } catch (error) {
          console.error("Error checking sessions:", error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };
    
    checkExistingSessions();
  }, [user]);

  useEffect(() => {
    // Check for successful payment
    if (searchParams.get('payment') === 'success') {
      toast({
        title: "Payment Successful!",
        description: "Please schedule your coaching session below.",
      });
    }
  }, [searchParams]);

  const handleBooking = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to schedule a coaching session.",
        variant: "destructive",
      });
      navigate("/login", { state: { returnTo: "/book-coaching" } });
      return;
    }

    try {
      setIsInitiatingPayment(true);
      const { data, error } = await supabase.functions.invoke('create-coaching-payment');
      
      if (error) throw error;
      if (!data.url) throw new Error('No checkout URL received');
      
      window.location.href = data.url;
    } catch (error) {
      console.error('Error creating payment session:', error);
      toast({
        title: "Error",
        description: "Could not initiate payment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsInitiatingPayment(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 container py-16">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <CoachingInfo />
            <CoachingPricing 
              isInitiatingPayment={isInitiatingPayment}
              onBookNow={handleBooking}
            />
            <CoachingNotifications 
              hasExistingSession={hasExistingSession}
              isAuthenticated={!!user}
            />
          </div>
          
          <CoachingCalendar 
            showCalendar={searchParams.get('payment') === 'success'}
            calendlyRef={calendlyRef}
          />
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BookCoaching;
